import { useState } from "react";
import type { Student } from "@/hooks/useStudents";

interface StudentPickerProps {
  students: Student[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

/**
 * Canlı derse katılacak öğrencileri seçmek için çoklu seçim listesi.
 * Öğrenci yoksa bilgilendirme gösterir.
 */
export default function StudentPicker({ students, selectedIds, onChange }: StudentPickerProps) {
  const [query, setQuery] = useState("");

  const filtered = students.filter((s) => {
    const q = query.trim().toLocaleLowerCase("tr-TR");
    if (!q) return true;
    return (
      s.name.toLocaleLowerCase("tr-TR").includes(q) ||
      s.email.toLocaleLowerCase("tr-TR").includes(q) ||
      s.grade.toLocaleLowerCase("tr-TR").includes(q)
    );
  });

  const toggle = (userId: string) => {
    const next = selectedIds.includes(userId)
      ? selectedIds.filter((id) => id !== userId)
      : [...selectedIds, userId];
    onChange(next);
  };

  const selectAll = () => {
    const allIds = filtered.map((s) => s.userId);
    const hasUnselected = allIds.some((id) => !selectedIds.includes(id));
    onChange(hasUnselected ? Array.from(new Set([...selectedIds, ...allIds])) : []);
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 border-b border-background-100">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" aria-hidden="true"></i>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Öğrenci ara..."
            className="w-full h-9 pl-8 pr-3 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 bg-background-50"
          />
        </div>
        <button
          type="button"
          onClick={selectAll}
          className="h-9 px-3 rounded-md text-xs font-semibold text-primary-700 bg-primary-100 hover:bg-primary-200 transition-colors whitespace-nowrap"
        >
          {filtered.every((s) => selectedIds.includes(s.userId)) ? "Seçimi Kaldır" : "Tümünü Seç"}
        </button>
      </div>

      <div className="max-h-56 overflow-y-auto p-2">
        {filtered.length === 0 ? (
          <div className="py-8 flex flex-col items-center justify-center text-center">
            <i className="ri-group-line text-2xl text-foreground-300" aria-hidden="true"></i>
            <p className="mt-2 text-sm text-foreground-500">
              {students.length === 0 ? "Henüz kayıtlı öğrenci yok." : "Aramayla eşleşen öğrenci bulunamadı."}
            </p>
          </div>
        ) : (
          filtered.map((s) => {
            const checked = selectedIds.includes(s.userId);
            return (
              <label
                key={s.id}
                className={`flex items-center gap-3 px-2.5 py-2 rounded-md cursor-pointer transition-colors ${
                  checked ? "bg-primary-100/70" : "hover:bg-background-100"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(s.userId)}
                  className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 accent-primary-500"
                />
                <span className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center shrink-0 text-xs font-semibold text-foreground-600">
                  {s.name.charAt(0).toLocaleUpperCase("tr-TR")}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-foreground-900 truncate">{s.name}</span>
                  <span className="block text-xs text-foreground-500 truncate">
                    {s.grade ? `${s.grade} · ` : ""}{s.email}
                  </span>
                </span>
                {checked && (
                  <i className="ri-check-line text-primary-600" aria-hidden="true"></i>
                )}
              </label>
            );
          })
        )}
      </div>

      <div className="px-3 py-2 border-t border-background-100 text-xs text-foreground-500">
        {selectedIds.length} öğrenci seçildi
      </div>
    </div>
  );
}